import React from 'react';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/styles';
import Color from 'color';
import { useCowbellTranslations } from '../../../../../../../../i18n/translations';
import { Show } from '../../../../../../../../components/Show';
import { AMENDATORY_ENDORSEMENT } from '../../constants';
import { useUiSettings } from '../../../../providers/UiSettingsProvider';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';
import { isCoverageEndorsementsState } from '../../types';
import {
  coverageInputColumns,
  useProposedColumn,
  type CoverageInputColumn,
} from '../../draft/coverageColumns';
import {
  UW_PANEL_HEADER_MIN_HEIGHT,
  UW_PANEL_HEADER_PADDING_Y,
  UW_PANEL_HEADING_FONT_SIZE,
  UW_PANEL_HEADING_WEIGHT,
  UW_PANEL_TITLE_FONT_SIZE,
  UW_INPUTS_INSET,
  UW_ROW_PADDING_X,
} from '../uwTileSpacing';

const CoverageGroupHeader = (props: CoverageGroupHeaderProps) => {
  const { t } = useCowbellTranslations();
  const { uiSettings } = useUiSettings();
  const IS_AMENDATORY = props.title === AMENDATORY_ENDORSEMENT;
  const context = useUnderwriterContext();
  const spans = useProposedColumn();

  return (
    <Grid
      container
      sx={{
        padding: `${UW_PANEL_HEADER_PADDING_Y} ${UW_ROW_PADDING_X}`,
        alignItems: 'center',
        minHeight: UW_PANEL_HEADER_MIN_HEIGHT,
        borderRadius: '4px 4px 0 0',

        backgroundColor: (theme) =>
          Color(theme.palette.background.default)
            .lighten(theme.palette.mode === 'dark' ? 0.25 : 0.02)
            .toString(),
      }}
    >
      <Grid
        item
        xs={spans.name}
        style={{
          paddingLeft: '0.5rem',
        }}
      >
        <Heading
          style={{ textAlign: 'left', fontSize: UW_PANEL_TITLE_FONT_SIZE }}
        >
          {props.children}
        </Heading>
      </Grid>

      <Show
        when={!IS_AMENDATORY && props.showStandardColumns}
        fallback={<Grid item xs={spans.limit + spans.inputs} />}
      >
        <Grid item xs={spans.limit}>
          <Box width="100%" display="flex" justifyContent="space-between">
            <Box style={{ fontWeight: UW_PANEL_HEADING_WEIGHT }}>Min</Box>

            <Heading>Coverage Limit</Heading>

            <Box style={{ fontWeight: UW_PANEL_HEADING_WEIGHT }}>Max</Box>
          </Box>
        </Grid>

        <Grid
          item
          xs={spans.inputs}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            pl={UW_INPUTS_INSET}
          >
            {coverageInputColumns(uiSettings).map((column) => (
              <InputHeading
                key={column}
                label={
                  column === 'retention' ? t('retention') : INPUT_LABELS[column]
                }
              />
            ))}
          </Box>
        </Grid>
      </Show>
      <Grid item xs={spans.premium}>
        <Heading
          style={{
            transform: 'translateX(-0.9rem)',
            textAlign: 'right',
          }}
        >
          {isCoverageEndorsementsState(context.state) ? 'Delta' : 'Premium'}
        </Heading>
      </Grid>
      <Show when={spans.proposed > 0}>
        <Grid item xs={spans.proposed}>
          <Heading
            style={{
              transform: 'translateX(-0.9rem)',
              textAlign: 'right',
            }}
          >
            Proposed
          </Heading>
        </Grid>
        {/* Deliberately empty, and deliberately a column. The accept/reject
            pair sits past Proposed; giving it width here is what keeps the
            heading over the figure it names rather than over the icons. */}
        <Grid item xs={spans.actions} />
      </Show>
    </Grid>
  );
};

export default CoverageGroupHeader;

type CoverageGroupHeaderProps = {
  children: React.ReactNode;
  title: string;
  showStandardColumns: boolean;
};

/** Headings for the input columns; Retention's comes from translations. */
const INPUT_LABELS: Record<
  Exclude<CoverageInputColumn, 'retention'>,
  string
> = {
  territorialLimit: 'Territorial Limit',
  basisOfCover: 'Basis of Cover',
  waitingPeriod: 'Waiting Period',
  retroactive: 'Retroactive Date',
};

/**
 * One input column's heading: one line, centred over its column. A column too
 * narrow for its name (five inputs on a narrow screen) ends in an ellipsis and
 * the full name is the tooltip, rather than the header growing a second line.
 */
const InputHeading = ({ label }: Readonly<{ label: string }>) => (
  <Heading
    width="100%"
    minWidth={0}
    title={label}
    sx={{
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {label}
  </Heading>
);

const Heading = styled(Box)(() => ({
  fontSize: UW_PANEL_HEADING_FONT_SIZE,
  fontWeight: UW_PANEL_HEADING_WEIGHT,
  textAlign: 'center',
}));
